import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FadeIn } from "../FadeIn";
import { useT } from "../../lib/i18n";

/**
 * Selected works — an editorial gallery of six images, each paired with
 * a short reflective caption ("légende") to give a contemplative,
 * art-viewer experience rather than a flat thumbnail grid.
 */
const SELECTED = [
  {
    id: "sel-1",
    src: "/photos/photo_07.jpg",
    titleKey: "sel.1.title",
    captionKey: "sel.1.caption",
    aspect: "aspect-[4/5]",
  },
  {
    id: "sel-2",
    src: "/photos/photo_05.jpg",
    titleKey: "sel.2.title",
    captionKey: "sel.2.caption",
    aspect: "aspect-[3/4]",
  },
  {
    id: "sel-3",
    src: "/photos/photo_42.jpg",
    titleKey: "sel.3.title",
    captionKey: "sel.3.caption",
    aspect: "aspect-[4/5]",
  },
  {
    id: "sel-4",
    src: "/photos/photo_85.jpg",
    titleKey: "sel.4.title",
    captionKey: "sel.4.caption",
    aspect: "aspect-[3/4]",
  },
  {
    id: "sel-5",
    src: "/photos/photo_36.jpg",
    titleKey: "sel.5.title",
    captionKey: "sel.5.caption",
    aspect: "aspect-[4/5]",
  },
  {
    id: "sel-6",
    src: "/photos/photo_08.jpg",
    titleKey: "sel.6.title",
    captionKey: "sel.6.caption",
    aspect: "aspect-[3/4]",
  },
];

const SelectedTile = ({ item, idx, t }) => (
  <FadeIn delay={idx * 0.08}>
    <figure
      data-testid={`selected-${item.id}`}
      data-cursor="VOIR"
      className={`group relative ${item.aspect} overflow-hidden bg-black md:cursor-none`}
    >
      <img
        src={item.src}
        alt={t(item.titleKey)}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
      />
      {/* Dark vignette + gradient for caption legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent opacity-95 group-hover:opacity-100 transition-opacity duration-700" />

      {/* Editorial index counter */}
      <div className="absolute top-5 left-5 flex items-center gap-3">
        <span className="font-display text-[11px] tracking-[0.32em] text-bone/70">
          {String(idx + 1).padStart(2, "0")}
        </span>
        <span className="h-px w-6 bg-stone/50" />
      </div>

      {/* Title + reflective caption */}
      <figcaption className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-bone">
        <div className="font-display text-2xl md:text-3xl italic leading-tight mb-3">
          {t(item.titleKey)}
        </div>
        {/* Reflective caption — slides in subtle on hover */}
        <div
          className="overflow-hidden text-sm md:text-base text-bone/80 max-w-sm leading-relaxed transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] max-h-0 opacity-0 group-hover:max-h-32 group-hover:opacity-100 group-hover:mt-1"
        >
          « {t(item.captionKey)} »
        </div>
        <div className="h-px w-10 bg-stone/60 mt-4 transition-all duration-700 group-hover:w-24" />
      </figcaption>
    </figure>
  </FadeIn>
);

export const SelectedWorksSection = () => {
  const t = useT();

  return (
    <section
      data-testid="selected-section"
      className="section-pad bg-ink relative overflow-hidden"
    >
      {/* Subtle warm radial accents */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(ellipse at 10% 0%, rgba(184,169,154,0.12) 0%, rgba(0,0,0,0) 55%), radial-gradient(ellipse at 90% 100%, rgba(184,169,154,0.08) 0%, rgba(0,0,0,0) 55%)",
        }}
      />

      <div className="container-x relative">
        <FadeIn>
          <div className="grid md:grid-cols-12 gap-6 md:gap-12 items-end mb-12 md:mb-16">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-stone/60" />
                <div className="eyebrow text-stone">
                  {t("selection.eyebrow")}
                </div>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl md:text-7xl leading-[0.95]">
                {t("selection.title")}
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-bone/65 text-sm md:text-base leading-relaxed max-w-md italic">
                {t("selection.intro")}
              </p>
            </div>
          </div>
        </FadeIn>

        {/* Editorial layout — horizontal snap-scroll on mobile, multi-col grid from sm+ */}
        <div className="flex sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6
                        overflow-x-auto sm:overflow-visible snap-x snap-mandatory scrollbar-hide
                        -mx-6 px-6 sm:mx-0 sm:px-0 pb-4 sm:pb-0">
          {SELECTED.map((item, i) => (
            <div
              key={item.id}
              className={`snap-center shrink-0 w-[78vw] sm:w-auto ${i % 3 === 1 ? "lg:translate-y-12" : ""}`}
            >
              <SelectedTile item={item} idx={i} t={t} />
            </div>
          ))}
        </div>

        <FadeIn delay={0.2}>
          <div className="mt-16 md:mt-24 lg:mt-32 flex items-center justify-between border-t border-bone/10 pt-8">
            <div className="text-[10px] tracking-[0.28em] uppercase text-bone/45">
              {t("selection.footer")}
            </div>
            <Link
              to="/portfolio"
              data-cursor="VOIR"
              data-testid="selected-cta"
              className="inline-flex items-center gap-3 text-stone hover:gap-5 transition-all"
            >
              <span className="text-[11px] tracking-[0.22em] uppercase">
                {t("selection.cta")}
              </span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};
